/**
 * intakeAssessmentPayloadFormModel.ts — Issue #343.
 *
 * Renamed from intakeAssessmentPayloadForm.ts so this helper module's
 * basename is distinct from IntakeAssessmentPayloadForm.tsx; the two
 * basenames collide case-insensitively on Windows / macOS default
 * filesystems and broke TypeScript module resolution + Vite imports on
 * those platforms.
 *
 * Structured payload helpers for the three intake assessment record kinds.
 * The backend keeps the `payload: string` contract; this module
 * deserializes the string into typed form values, validates user input
 * field by field, and re-serializes back to a deterministic JSON string
 * (stable key order, no unrelated keys).
 *
 * Red lines:
 *   - Probability is kept as a Decimal string (decimal.js) and never
 *     touched by native JS float arithmetic.
 *   - `preferredProjects`, `labResults`, `imagingResults` are serialized
 *     as JSON arrays of strings with a documented, deterministic split.
 *   - Unknown / unmappable historical payload yields a `legacy_invalid`
 *     parse result so the UI can show a controlled warning and block
 *     silent loss of the original blob.
 */
import { Decimal } from 'decimal.js';
import type { IntakeAssessmentRecordKind } from '@/hooks/useIntakeAssessment';

// ============================================================================
// Field types per record kind
// ============================================================================

export interface GoldenAnalysisValues {
  painPoints: string;
  budgetEstimate: string;
  decisionMakers: string;
  concerns: string;
  trustAnchors: string;
  nextStep: string;
}

export interface DealIntentionValues {
  intentLevel: string;
  /** Decimal string in the closed interval [0, 1], e.g. "0.75". */
  probability: string;
  /** ISO date string (YYYY-MM-DD) or empty. */
  expectedClosingDate: string;
  preferredProjects: string[];
  notes: string;
}

export interface HealthAssessmentValues {
  medicalHistorySummary: string;
  physicalExamFindings: string;
  labResults: string[];
  imagingResults: string[];
  immuneAssessment: string;
  doctorNotes: string;
}

export type IntakeAssessmentPayloadValuesByKind = {
  golden_analysis: GoldenAnalysisValues;
  deal_intention: DealIntentionValues;
  health_assessment: HealthAssessmentValues;
};

export type IntakeAssessmentPayloadValues =
  IntakeAssessmentPayloadValuesByKind[IntakeAssessmentRecordKind];

/**
 * Discriminated-union form state — keeps `kind` and `values` correlated
 * so React state setters and the form component can narrow without
 * unsafe casts.
 */
export type IntakeAssessmentPayloadFormState =
  | { kind: 'golden_analysis'; values: GoldenAnalysisValues }
  | { kind: 'deal_intention'; values: DealIntentionValues }
  | { kind: 'health_assessment'; values: HealthAssessmentValues };

export function createDefaultFormState(
  kind: 'golden_analysis',
): { kind: 'golden_analysis'; values: GoldenAnalysisValues };
export function createDefaultFormState(
  kind: 'deal_intention',
): { kind: 'deal_intention'; values: DealIntentionValues };
export function createDefaultFormState(
  kind: 'health_assessment',
): { kind: 'health_assessment'; values: HealthAssessmentValues };
export function createDefaultFormState(
  kind: IntakeAssessmentRecordKind,
): IntakeAssessmentPayloadFormState;
export function createDefaultFormState(
  kind: IntakeAssessmentRecordKind,
): IntakeAssessmentPayloadFormState {
  switch (kind) {
    case 'golden_analysis':
      return { kind, values: { ...GOLDEN_ANALYSIS_DEFAULTS } };
    case 'deal_intention':
      return { kind, values: { ...DEAL_INTENTION_DEFAULTS } };
    case 'health_assessment':
      return { kind, values: { ...HEALTH_ASSESSMENT_DEFAULTS } };
    default: {
      const exhaustive: never = kind;
      throw new Error(
        `Unknown intake assessment record kind: ${String(exhaustive)}`,
      );
    }
  }
}

// ============================================================================
// Defaults
// ============================================================================

export const GOLDEN_ANALYSIS_DEFAULTS: GoldenAnalysisValues = {
  painPoints: '',
  budgetEstimate: '',
  decisionMakers: '',
  concerns: '',
  trustAnchors: '',
  nextStep: '',
};

export const DEAL_INTENTION_DEFAULTS: DealIntentionValues = {
  intentLevel: '',
  probability: '0.00',
  expectedClosingDate: '',
  preferredProjects: [],
  notes: '',
};

export const HEALTH_ASSESSMENT_DEFAULTS: HealthAssessmentValues = {
  medicalHistorySummary: '',
  physicalExamFindings: '',
  labResults: [],
  imagingResults: [],
  immuneAssessment: '',
  doctorNotes: '',
};

export function defaultPayloadValues<K extends IntakeAssessmentRecordKind>(
  kind: K,
): IntakeAssessmentPayloadValuesByKind[K] {
  switch (kind) {
    case 'golden_analysis':
      return { ...GOLDEN_ANALYSIS_DEFAULTS } as IntakeAssessmentPayloadValuesByKind[K];
    case 'deal_intention':
      return { ...DEAL_INTENTION_DEFAULTS } as IntakeAssessmentPayloadValuesByKind[K];
    case 'health_assessment':
      return { ...HEALTH_ASSESSMENT_DEFAULTS } as IntakeAssessmentPayloadValuesByKind[K];
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown intake assessment record kind: ${String(exhaustive)}`);
    }
  }
}

// ============================================================================
// Parse result discriminator
// ============================================================================

/**
 * Parse result.
 *
 * - `ok` — payload mapped cleanly to typed values; unknown keys (if any)
 *   were dropped because the contract is field-oriented.
 * - `legacy_invalid` — payload existed and was non-empty but cannot be
 *   safely mapped: JSON parse failure, wrong shape, type drift, etc.
 *   `defaults` carries fresh defaults so the form is renderable; the
 *   raw original blob is kept for the controlled warning panel. The UI
 *   must NOT serialize `defaults` back over a legacy_invalid record
 *   without an explicit operator reset action.
 */
export type PayloadParseResult<K extends IntakeAssessmentRecordKind> =
  | {
      kind: 'ok';
      values: IntakeAssessmentPayloadValuesByKind[K];
    }
  | {
      kind: 'legacy_invalid';
      defaults: IntakeAssessmentPayloadValuesByKind[K];
      raw: string;
      reason: string;
    };

// ============================================================================
// Helpers — deterministic split / join for multi-value text fields
// ============================================================================

/**
 * Split a free-form textarea string into a trimmed string[] using
 * newline OR comma as separators. Empty entries are dropped. Order is
 * preserved. Used as a documented fallback when the historical payload
 * stored a single string for `preferredProjects` / `labResults` /
 * `imagingResults`.
 */
export function splitMultiLineList(input: string): string[] {
  if (!input) return [];
  return input
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Join a string[] into a textarea-friendly newline-joined string.
 * Used only for rendering; the serialized payload always stores arrays.
 */
export function joinMultiLineList(values: readonly string[]): string {
  return values.join('\n');
}

// ============================================================================
// Type guards
// ============================================================================

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readStringArray(value: unknown): string[] | null {
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (const item of value) {
      if (typeof item !== 'string') return null;
      const trimmed = item.trim();
      if (trimmed.length > 0) out.push(trimmed);
    }
    return out;
  }
  if (typeof value === 'string') {
    return splitMultiLineList(value);
  }
  return null;
}

function normalizeProbability(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  try {
    const dec = new Decimal(value);
    if (dec.isNaN() || dec.lt(0) || dec.gt(1)) return '';
    return dec.toFixed(2);
  } catch {
    return '';
  }
}

// ============================================================================
// Per-kind parsers
// ============================================================================

function parseGoldenAnalysis(
  obj: Record<string, unknown>,
): GoldenAnalysisValues {
  return {
    painPoints: readString(obj.painPoints),
    budgetEstimate: readString(obj.budgetEstimate),
    decisionMakers: readString(obj.decisionMakers),
    concerns: readString(obj.concerns),
    trustAnchors: readString(obj.trustAnchors),
    nextStep: readString(obj.nextStep),
  };
}

function parseDealIntention(
  obj: Record<string, unknown>,
): DealIntentionValues | null {
  const preferred = readStringArray(obj.preferredProjects);
  if (preferred === null && obj.preferredProjects !== undefined) {
    return null;
  }
  return {
    intentLevel: readString(obj.intentLevel),
    probability:
      obj.probability === undefined || obj.probability === null
        ? DEAL_INTENTION_DEFAULTS.probability
        : normalizeProbability(obj.probability),
    expectedClosingDate: readString(obj.expectedClosingDate),
    preferredProjects: preferred ?? [],
    notes: readString(obj.notes),
  };
}

function parseHealthAssessment(
  obj: Record<string, unknown>,
): HealthAssessmentValues | null {
  const labs = readStringArray(obj.labResults);
  if (labs === null && obj.labResults !== undefined) return null;
  const imaging = readStringArray(obj.imagingResults);
  if (imaging === null && obj.imagingResults !== undefined) return null;
  return {
    medicalHistorySummary: readString(obj.medicalHistorySummary),
    physicalExamFindings: readString(obj.physicalExamFindings),
    labResults: labs ?? [],
    imagingResults: imaging ?? [],
    immuneAssessment: readString(obj.immuneAssessment),
    doctorNotes: readString(obj.doctorNotes),
  };
}

// ============================================================================
// Public parse / serialize
// ============================================================================

/**
 * Parse a stored payload string for the given record kind.
 *
 * Empty / whitespace-only payloads are treated as fresh defaults (a
 * brand new draft case), not as legacy_invalid.
 */
export function parsePayload<K extends IntakeAssessmentRecordKind>(
  kind: K,
  raw: string | null | undefined,
): PayloadParseResult<K> {
  if (raw == null || raw.trim() === '') {
    return {
      kind: 'ok',
      values: defaultPayloadValues(kind),
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return {
      kind: 'legacy_invalid',
      defaults: defaultPayloadValues(kind),
      raw,
      reason:
        err instanceof Error
          ? `历史详情不是合法 JSON：${err.message}`
          : '历史详情不是合法 JSON',
    };
  }

  if (!isRecord(parsed)) {
    return {
      kind: 'legacy_invalid',
      defaults: defaultPayloadValues(kind),
      raw,
      reason: '历史详情不是对象结构，无法映射到结构化表单。',
    };
  }

  switch (kind) {
    case 'golden_analysis': {
      const values = parseGoldenAnalysis(parsed);
      return {
        kind: 'ok',
        values: values as IntakeAssessmentPayloadValuesByKind[K],
      };
    }
    case 'deal_intention': {
      const values = parseDealIntention(parsed);
      if (!values) {
        return {
          kind: 'legacy_invalid',
          defaults: defaultPayloadValues(kind),
          raw,
          reason: '历史成交意向详情字段类型异常，无法映射到结构化表单。',
        };
      }
      return {
        kind: 'ok',
        values: values as IntakeAssessmentPayloadValuesByKind[K],
      };
    }
    case 'health_assessment': {
      const values = parseHealthAssessment(parsed);
      if (!values) {
        return {
          kind: 'legacy_invalid',
          defaults: defaultPayloadValues(kind),
          raw,
          reason: '历史接诊评估详情字段类型异常，无法映射到结构化表单。',
        };
      }
      return {
        kind: 'ok',
        values: values as IntakeAssessmentPayloadValuesByKind[K],
      };
    }
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown intake assessment record kind: ${String(exhaustive)}`);
    }
  }
}

/**
 * Discriminated-union parse result for the detail page. The values are
 * paired with their kind so React state setters and the form component
 * can narrow without unsafe casts.
 */
export type PayloadFormParseResult =
  | { kind: 'ok'; state: IntakeAssessmentPayloadFormState }
  | {
      kind: 'legacy_invalid';
      defaults: IntakeAssessmentPayloadFormState;
      raw: string;
      reason: string;
    };

export function parsePayloadToFormState(
  recordKind: IntakeAssessmentRecordKind,
  raw: string | null | undefined,
): PayloadFormParseResult {
  switch (recordKind) {
    case 'golden_analysis': {
      const r = parsePayload('golden_analysis', raw);
      if (r.kind === 'ok') {
        return { kind: 'ok', state: { kind: 'golden_analysis', values: r.values } };
      }
      return {
        kind: 'legacy_invalid',
        defaults: { kind: 'golden_analysis', values: r.defaults },
        raw: r.raw,
        reason: r.reason,
      };
    }
    case 'deal_intention': {
      const r = parsePayload('deal_intention', raw);
      if (r.kind === 'ok') {
        return { kind: 'ok', state: { kind: 'deal_intention', values: r.values } };
      }
      return {
        kind: 'legacy_invalid',
        defaults: { kind: 'deal_intention', values: r.defaults },
        raw: r.raw,
        reason: r.reason,
      };
    }
    case 'health_assessment': {
      const r = parsePayload('health_assessment', raw);
      if (r.kind === 'ok') {
        return { kind: 'ok', state: { kind: 'health_assessment', values: r.values } };
      }
      return {
        kind: 'legacy_invalid',
        defaults: { kind: 'health_assessment', values: r.defaults },
        raw: r.raw,
        reason: r.reason,
      };
    }
    default: {
      const exhaustive: never = recordKind;
      throw new Error(
        `Unknown intake assessment record kind: ${String(exhaustive)}`,
      );
    }
  }
}

/**
 * Serialize a discriminated form state to the backend `payload` string.
 * Convenience wrapper around `serializePayload` that picks the matching
 * kind without unsafe casts on the caller side.
 */
export function serializeFormState(
  state: IntakeAssessmentPayloadFormState,
): string {
  switch (state.kind) {
    case 'golden_analysis':
      return serializePayload('golden_analysis', state.values);
    case 'deal_intention':
      return serializePayload('deal_intention', state.values);
    case 'health_assessment':
      return serializePayload('health_assessment', state.values);
    default: {
      const exhaustive: never = state;
      throw new Error(
        `Unknown intake assessment record kind: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}

export function validateFormState(
  state: IntakeAssessmentPayloadFormState,
): PayloadValidationError[] {
  switch (state.kind) {
    case 'golden_analysis':
      return validatePayloadValues('golden_analysis', state.values);
    case 'deal_intention':
      return validatePayloadValues('deal_intention', state.values);
    case 'health_assessment':
      return validatePayloadValues('health_assessment', state.values);
    default: {
      const exhaustive: never = state;
      throw new Error(
        `Unknown intake assessment record kind: ${JSON.stringify(exhaustive)}`,
      );
    }
  }
}

/**
 * Serialize structured values to a deterministic JSON string. Keys are
 * emitted in the order declared in the per-kind interface above; no
 * unrelated keys are added; arrays preserve user-entered order.
 */
export function serializePayload<K extends IntakeAssessmentRecordKind>(
  kind: K,
  values: IntakeAssessmentPayloadValuesByKind[K],
): string {
  switch (kind) {
    case 'golden_analysis': {
      const v = values as GoldenAnalysisValues;
      return JSON.stringify({
        painPoints: v.painPoints,
        budgetEstimate: v.budgetEstimate,
        decisionMakers: v.decisionMakers,
        concerns: v.concerns,
        trustAnchors: v.trustAnchors,
        nextStep: v.nextStep,
      });
    }
    case 'deal_intention': {
      const v = values as DealIntentionValues;
      return JSON.stringify({
        intentLevel: v.intentLevel,
        probability: v.probability,
        expectedClosingDate: v.expectedClosingDate,
        preferredProjects: [...v.preferredProjects],
        notes: v.notes,
      });
    }
    case 'health_assessment': {
      const v = values as HealthAssessmentValues;
      return JSON.stringify({
        medicalHistorySummary: v.medicalHistorySummary,
        physicalExamFindings: v.physicalExamFindings,
        labResults: [...v.labResults],
        imagingResults: [...v.imagingResults],
        immuneAssessment: v.immuneAssessment,
        doctorNotes: v.doctorNotes,
      });
    }
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown intake assessment record kind: ${String(exhaustive)}`);
    }
  }
}

// ============================================================================
// Field-level validation (user-facing)
// ============================================================================

export interface PayloadValidationError {
  /** Field key under the per-kind values object. */
  field: string;
  /** User-facing Chinese message. */
  message: string;
}

export function validatePayloadValues<K extends IntakeAssessmentRecordKind>(
  kind: K,
  values: IntakeAssessmentPayloadValuesByKind[K],
): PayloadValidationError[] {
  switch (kind) {
    case 'golden_analysis':
      return [];
    case 'deal_intention': {
      const v = values as DealIntentionValues;
      const errors: PayloadValidationError[] = [];
      if (v.probability.trim() !== '') {
        try {
          const dec = new Decimal(v.probability);
          if (dec.isNaN() || dec.lt(0) || dec.gt(1)) {
            errors.push({
              field: 'probability',
              message: '成交概率必须是 0 到 1 之间的小数（例如 0.75）。',
            });
          }
        } catch {
          errors.push({
            field: 'probability',
            message: '成交概率必须是合法的小数字符串。',
          });
        }
      }
      if (v.expectedClosingDate.trim() !== '') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v.expectedClosingDate.trim())) {
          errors.push({
            field: 'expectedClosingDate',
            message: '预计成交日期必须是 YYYY-MM-DD 格式。',
          });
        }
      }
      return errors;
    }
    case 'health_assessment':
      return [];
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown intake assessment record kind: ${String(exhaustive)}`);
    }
  }
}
